// hooks/usePendingHandovers.ts
// Returns a live count of pending cash_handover requests for the sidebar badge.
// Requires cash_handovers to be in the supabase_realtime publication
// (see migration enable_realtime_requests_handovers).

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";

export function usePendingHandovers(ownerId: string | undefined) {
  const [pendingCount, setPendingCount] = useState(0);
  const ownerRef = useRef(ownerId);

  useEffect(() => { ownerRef.current = ownerId; }, [ownerId]);

  const refetch = useCallback(async (uid: string) => {
    const { count } = await supabase
      .from("cash_handovers")
      .select("id", { count: "exact", head: true })
      .eq("owner_id", uid)
      .eq("status", "pending");
    setPendingCount(count ?? 0);
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!ownerId) return;
    refetch(ownerId);
  }, [ownerId, refetch]);

  // Realtime updates
  useEffect(() => {
    const ch = supabase
      .channel("pending-handovers-badge")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cash_handovers" },
        (payload) => {
          const uid = ownerRef.current;
          if (!uid) return;
          const record = (payload.new ?? payload.old) as any;
          if (record?.owner_id !== uid) return;
          refetch(uid);
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [refetch]);

  return pendingCount;
}
